import { routes } from "../../app/routes";
import {useCRUDHandlers, 
    useGridActions, 
    usePermission, 
    useSetPageTitle,
    useSharedState,
} from "../../hooks";
import { SubscriptionService } from "../../services";
import { Breakpoints, Button, ConfirmationModal, Icon, IconAndText, IconDefinitions, ODataGrid, ResponsiveGridColumn, TitlelessCard } from "../../components";
import { GridCellProps, MenuItem } from "@progress/kendo-react-all";
import { usePageActions } from "../PageLayout";
import { PermissionDefinitions } from "../../security";
import { useSelector } from "react-redux";
import { getUserTenantId } from "../../app";
import { useCallback } from "react";
import { SubscriptionModel } from "../../data/entities/billing";
import { AddSubscriptionModal, GridFormattedDateCell } from "./components";
import { createODataQueryFilter } from "../../utils";


export function HostSubscriptionsPage() {  
    const gridData = useSharedState<Array<SubscriptionModel>>([]);
    const subscriptionService = new SubscriptionService();
    const { onClick, onSelect, showAddState, showDelete, showDeleteState, deleteItemState } = useGridActions<SubscriptionModel>(routes.host.subscription.details);
    const { gridRef, getItems, addItem, deleteItem } = useCRUDHandlers<SubscriptionModel>(subscriptionService, "Subscription", showAddState);
    const tenantId = useSelector(getUserTenantId());
    const canAddSubscription = usePermission(PermissionDefinitions.host.create());
    const canDeleteSubscription = usePermission(PermissionDefinitions.host.delete(tenantId!));

    useSetPageTitle("Subscriptions", []);

    usePageActions(
      canAddSubscription && (
        <Button
          onClick={() => {
            showAddState.setter(true);
          }}
        >
          <IconAndText iconName={IconDefinitions.add} text="Add" />
        </Button>
      )
      , [canAddSubscription]);
  
    const commandsCell = useCallback(
      (props: GridCellProps) => (
        <>
        {canDeleteSubscription && (
          <td className="text-center">
            <Icon
              iconName={IconDefinitions.delete}
              className="text-danger"
              onClick={() => showDelete(props.dataItem)}
            />
          </td>
        )}
        </>
      ),
      [showDelete, canDeleteSubscription]
    );

    return (
     <TitlelessCard bodyClassName="p-0">
      <ODataGrid
        ref={gridRef}
        getData={(oDataQuery) => getItems(createODataQueryFilter(oDataQuery))}
        dataState={gridData}
        onRowClick={onClick}
        onRowMenuSelect={onSelect}
        sort={[{ field: "name", dir: "asc"}]}
        scrollable="scrollable"
        pageSize={10}
       >
        <ResponsiveGridColumn
          title="Name"
          field="name"
          sortable={true}
        />
         <ResponsiveGridColumn
          title="Price"
          field="price"
          sortable={true}
        />
        <ResponsiveGridColumn
          title="Frequency"
          field="frequency"
          sortable={true}
        />
        <ResponsiveGridColumn
          title="Start Date"
          field="startDate"
          sortable={true}
          cell={(props) => <GridFormattedDateCell {...props} field="startDate"/>}   
        />
        <ResponsiveGridColumn
              className="column-url"
              field="url"
              title=" "
              sortable={false}
              filterable={false}
              headerCell={() => null}
              cell={canDeleteSubscription === true ? commandsCell : undefined}
              width={50}
              breakpoint={Breakpoints.sm}
        />

        <MenuItem
          text="Open"
          data={{ action: "open" }}
          icon="hyperlink-open"
        />
        <MenuItem
          text="Open Tab"
          data={{ action: "opentab" }}
          icon="windows"
        />
        {
          canDeleteSubscription && (
              <MenuItem
                text="Delete"
                data={{ action: "delete" }}
                icon={IconDefinitions.delete}
              />
            )
        }
      </ODataGrid>
      
      <AddSubscriptionModal
          showAddState={showAddState}
          showState={showAddState}
          onSubmit={addItem}
      />

      <ConfirmationModal
          showState={showDeleteState}
          title={`Delete ${deleteItemState.value?.name}`}
          message={`You are about to delete this subscription, it cannot be undone. Are you sure you want to delete ${deleteItemState.value.name}?`}
          confirmText="Delete"
          onConfirm={() => deleteItem(deleteItemState.value)}
      />
     </TitlelessCard>
    );
}

export default HostSubscriptionsPage;
